'use client';

import { useSyncExternalStore } from 'react';
import { THEME_STORAGE_KEY } from './theme.constants';

export type Theme = 'light' | 'dark';

const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
    listeners.add(callback);
    window.addEventListener('storage', callback);
    return () => {
        listeners.delete(callback);
        window.removeEventListener('storage', callback);
    };
}

function getSnapshot(): Theme {
    const el = document.documentElement;
    if (el.classList.contains('dark-mode')) return 'dark';
    if (el.classList.contains('light-mode')) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getServerSnapshot(): Theme {
    // Deterministic during SSR + hydration; the real value is read right after.
    return 'light';
}

/** Applies an explicit .dark-mode/.light-mode class on <html> (overriding the
 *  system-preference fallback), persists the choice, and notifies subscribers. */
export function setTheme(next: Theme) {
    const el = document.documentElement;
    el.classList.toggle('dark-mode', next === 'dark');
    el.classList.toggle('light-mode', next === 'light');
    try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
        // ignore storage errors (private mode etc.)
    }
    listeners.forEach((listener) => listener());
}

/** Reactive current theme. Shared store so every toggle (header icon button,
 *  off-canvas segmented switch) stays in sync. */
export function useTheme(): Theme {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
